import {
  BalanceDueTaskBackfillResult,
  BalanceDueTaskDetail,
  PendingBalanceDueTask,
  BulkUpdateTaskPayload,
  CompleteHealthCheckReviewPayload,
  CreatedFilingsPastApprovalCap,
  HealthCheckAggregateOutreachPayload,
  HealthCheckAggregateOutreachPreviewPayload,
  HealthCheckAggregateOutreachPreviewResponse,
  HealthCheckAggregateOutreachResponse,
  HealthCheckDisposition,
  HealthCheckOutreachPayload,
  HealthCheckOutreachResponse,
  HealthCheckOutreachSendersResponse,
  PartnerTask,
  Task,
  UpdateTaskPayload,
} from '@/types/tasks';
import api, { API_PREFIX } from './api';
import { ADMIN_API_REQUEST_TIMEOUT_MS } from './request-timeouts';

export const TASKS_BY_FILING_ID_STATE_KEY = 'tasks_by_filing_id';
export const TASKS_BY_REGISTRATION_ID_STATE_KEY = 'tasks_by_registration_id';
export const TASKS_ALL_FILING_STATE_KEY = 'tasks_all_filing';
// Own prefix so task, bulk and payment mutations, which invalidate TASKS_ALL_FILING_STATE_KEY,
// do not refetch the special handling count: it does not depend on any task's status.
export const TASKS_FILING_SPECIAL_HANDLING_COUNT_STATE_KEY = 'tasks_filing_special_handling_count';
export const TASKS_FILING_LIST_STATE_KEY = 'tasks_filing_list';
export const TASKS_ALL_REGISTRATION_STATE_KEY = 'tasks_all_registration';
export const TASKS_REGISTRATION_LIST_STATE_KEY = 'tasks_registration_list';
export const TASKS_ORGANIZATION_STATE_KEY = 'tasks_organization';
export const TASKS_PARTNER_STATE_KEY = 'tasks_partner';
export const aggregateOutreachSendMutationKey = (orgId: string) => ['health-check-aggregate-send', orgId] as const;

export const updateTask = (taskId: string, orgId: string, task: UpdateTaskPayload) => {
  return api.put<UpdateTaskPayload>(`${API_PREFIX}/tasks/${taskId}`, task, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const bulkUpdateTasks = (payload: BulkUpdateTaskPayload) => {
  return api.put(`${API_PREFIX}/tasks/bulk-update`, payload);
};

export const getTaskByFilingId = (filingId: string, orgId: string) => {
  return api.get<Task>(`${API_PREFIX}/tasks/filing/${filingId}`, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const getFilingTasks = (params?: Record<string, any>) => {
  return api.get(`${API_PREFIX}/tasks/filings`, {
    params,
  });
};

export const getRegistrationTasks = (params?: Record<string, any>) => {
  return api.get(`${API_PREFIX}/tasks/registrations`, {
    params,
  });
};

export const getOrganizationTasks = (orgId: string) => {
  return api.get(`${API_PREFIX}/tasks/organization-health-check`, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const openOrganizationHealthCheck = (orgId: string) => {
  return api.post(`${API_PREFIX}/tasks/organization-health-check`, undefined, {
    headers: {
      'x-organization-id': orgId,
    },
  });
};

export const getKintsugiTasks = (params?: Record<string, unknown>) => {
  return api.get(`${API_PREFIX}/tasks/kintsugi-tasks`, { params });
};

export const getCreatedFilingsPastApprovalCap = () => {
  return api.get<CreatedFilingsPastApprovalCap>(`${API_PREFIX}/tasks/filings/created-after-approval-cap`, {
    timeout: ADMIN_API_REQUEST_TIMEOUT_MS,
  });
};

export const exportCreatedFilingsPastApprovalCap = () => {
  return api.get<Blob>(`${API_PREFIX}/tasks/filings/created-after-approval-cap/export`, {
    responseType: 'blob',
    timeout: ADMIN_API_REQUEST_TIMEOUT_MS,
  });
};

export const exportKintsugiTasks = (params?: Record<string, unknown>) => {
  return api.get(`${API_PREFIX}/tasks/kintsugi-tasks/export`, {
    responseType: 'blob',
    params,
  });
};

export const getKintsugiCxTasks = (params?: Record<string, unknown>) => {
  return api.get(`${API_PREFIX}/tasks/kintsugi-cx`, { params });
};

export const exportKintsugiCxTasks = (params?: Record<string, unknown>) => {
  return api.get(`${API_PREFIX}/tasks/kintsugi-cx/export`, {
    responseType: 'blob',
    params,
  });
};

export const getHealthCheckReview = (orgId: string) => {
  return api.get(`${API_PREFIX}/tasks/organization-health-check/review`, {
    headers: { 'x-organization-id': orgId },
  });
};

export const completeHealthCheckReview = (orgId: string, body: CompleteHealthCheckReviewPayload) => {
  return api.post(`${API_PREFIX}/tasks/organization-health-check/complete`, body, {
    headers: { 'x-organization-id': orgId },
  });
};

// Persist a single disposition (e.g. an ignore) immediately, without completing the
// review — the wizard calls this on Save so the ignore and its system note land now.
export const recordHealthCheckDisposition = (orgId: string, body: HealthCheckDisposition) => {
  return api.post(`${API_PREFIX}/tasks/organization-health-check/disposition`, body, {
    headers: { 'x-organization-id': orgId },
  });
};

export const sendHealthCheckOutreach = (orgId: string, payload: HealthCheckOutreachPayload) => {
  // API_PREFIX is already '/v1/admin'; the backend serves this at
  // '/v1/admin/tasks/...'. Prefixing another '/admin' here produced
  // '/v1/admin/admin/tasks/...' and 404'd every outreach send.
  return api.post<HealthCheckOutreachResponse>(`${API_PREFIX}/tasks/organization-health-check/outreach`, payload, {
    headers: { 'x-organization-id': orgId },
  });
};

export const sendHealthCheckAggregateOutreach = (orgId: string, payload: HealthCheckAggregateOutreachPayload) => {
  return api.post<HealthCheckAggregateOutreachResponse>(
    `${API_PREFIX}/tasks/organization-health-check/outreach/aggregate`,
    payload,
    {
      headers: { 'x-organization-id': orgId },
    }
  );
};

export const previewHealthCheckAggregateOutreach = (
  orgId: string,
  payload: HealthCheckAggregateOutreachPreviewPayload
) => {
  return api.post<HealthCheckAggregateOutreachPreviewResponse>(
    `${API_PREFIX}/tasks/organization-health-check/outreach/aggregate/preview`,
    payload,
    {
      headers: { 'x-organization-id': orgId },
    }
  );
};

export const getHealthCheckOutreachSenders = (orgId: string) => {
  return api.get<HealthCheckOutreachSendersResponse>(`${API_PREFIX}/tasks/organization-health-check/outreach/senders`, {
    headers: { 'x-organization-id': orgId },
  });
};

export const getPartnerHealthCheckTask = (partnerId: string) => {
  return api.get<PartnerTask>(`${API_PREFIX}/partner/tasks/health-check`, {
    params: { partner_id: partnerId },
  });
};

export const updatePartnerTask = (
  taskId: string,
  task: Pick<UpdateTaskPayload, 'assignee' | 'waiting_on' | 'status'>
) => {
  return api.put<PartnerTask>(`${API_PREFIX}/partner/tasks/${taskId}`, task);
};

export const BALANCE_DUE_TASK_DETAIL_STATE_KEY = 'balance_due_task_detail';

// The task id comes from a shareable ?balance_due_task= link, so it is encoded to keep
// it inside its own path segment. '.' and '..' pass through encoding unchanged and
// would still walk up the path, so they are rejected.
const balanceDueTaskPath = (taskId: string, action = '') => {
  if (taskId === '.' || taskId === '..') throw new Error('Invalid balance-due task id');
  return `${API_PREFIX}/tasks/balance-due/${encodeURIComponent(taskId)}${action}`;
};

export const getBalanceDueTaskDetail = (taskId: string, orgId: string) => {
  return api.get<BalanceDueTaskDetail>(balanceDueTaskPath(taskId), {
    headers: { 'x-organization-id': orgId },
  });
};

export const markBalanceDueTaskPaid = (taskId: string, orgId: string, amountPaid: string) => {
  return api.post(
    balanceDueTaskPath(taskId, '/mark-paid'),
    { amount_paid: amountPaid },
    { headers: { 'x-organization-id': orgId } }
  );
};

export const updateBalanceDueAmountPaid = (taskId: string, orgId: string, amountPaid: string) => {
  return api.put(
    balanceDueTaskPath(taskId, '/amount-paid'),
    { amount_paid: amountPaid },
    { headers: { 'x-organization-id': orgId } }
  );
};

export const backfillBalanceDueTasks = (orgId: string) => {
  return api.post<BalanceDueTaskBackfillResult>(`${API_PREFIX}/tasks/balance-due/backfill`, null, {
    headers: { 'x-organization-id': orgId },
  });
};

export const OPEN_BALANCE_DUE_TASKS_STATE_KEY = 'open_balance_due_tasks';

export const getOpenBalanceDueTasksForRegistration = (registrationId: string, orgId: string) => {
  return api.get<PendingBalanceDueTask[]>(
    `${API_PREFIX}/tasks/balance-due/registration/${encodeURIComponent(registrationId)}`,
    {
      headers: { 'x-organization-id': orgId },
    }
  );
};

export const reassignBalanceDueTask = (taskId: string, orgId: string, assignee: string) => {
  return api.put(
    balanceDueTaskPath(taskId, '/assignee'),
    { assignee },
    {
      headers: { 'x-organization-id': orgId },
    }
  );
};

export const getTaskByRegistrationId = (registrationId: string, orgId: string, size?: number) => {
  return api.get(`${API_PREFIX}/tasks/registration/${registrationId}`, {
    headers: {
      'x-organization-id': orgId,
    },
    params: size ? { size } : undefined,
  });
};
